"use client";

import { EventTrackingProps } from "@/types/common";
import { useSearchParams } from "next/navigation";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import ErrorBoundary from "@/components/common/errorBoundary";

import "../applied/appliedJobCard/components/styles.css";
import { ColumnType, appliedPageLayoutConfig } from "../config";
import { useDashboardContext } from "../index";
import { appliedPageMap } from "../map";
import {
  AppliedJobAPIKeysType,
  AppliedPageContextProps,
  AppliedPageDataType,
  FilterStatusType,
} from "./types";

const AppliedPageContext = createContext<AppliedPageContextProps>({
  appliedJobData: {},
  setAppliedJobData: () => {},
  handleEventTracking: (event: EventTrackingProps) => {},
  cleverTapPropertyValues: {
    applied: "",
    recommended: "",
    saved: "",
  },
});

export function useAppliedPageData() {
  return useContext(AppliedPageContext);
}

const AppliedPage = () => {
  const {
    isMobile,
    jobServerData,
    handleEventTracking,
    cleverTapPropertyValues,
  } = useDashboardContext();
  const searchParams = useSearchParams();

  const filterStatusPresent =
    (jobServerData.jobData?.filterStatus &&
      Array.isArray(jobServerData.jobData.filterStatus) &&
      jobServerData.jobData.filterStatus.filter(
        (data: FilterStatusType, index: number) => {
          return data.count > 0;
        },
      ).length > 0) ||
    false;

  const [appliedJobData, setAppliedJobData] = useState<AppliedPageDataType>(
    () => {
      const appliedTempData = {
        ...jobServerData.jobData,
        siteProps: jobServerData.siteProps,
        sitePropsProperties: jobServerData.sitePropsProperties,
        filterStatusPresent: filterStatusPresent,
        profilePersonalDetails: jobServerData.profilePersonalDetails,
        isLoadingJobDesc: true,
        jobDescIndex: { selectedIndex: null },
        showMobileModal: false,
        autoApplyData: jobServerData.autoApplyData,
        similarJobsSelected: false,
        activeTab: jobServerData.activeTab,
        newDataLoader: jobServerData.clientTabSwitch,
        urlQueries: {
          jobId: undefined,
          applicationId: undefined,
        },
        dashboardHeaderRef: jobServerData.dashboardHeaderRef,
      };
      return appliedTempData;
    },
  );

  const [loader, setLoader] = useState(true);

  const layout = useMemo(() => {
    if (!isMobile) {
      return appliedPageLayoutConfig.layouts.desktop;
    }
    return appliedPageLayoutConfig.layouts.mobile;
  }, [isMobile]);

  const contextValue = useMemo(
    () => ({
      appliedJobData,
      setAppliedJobData,
      handleEventTracking,
      cleverTapPropertyValues,
    }),
    [appliedJobData, handleEventTracking],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const activeTabClient = params.get("activeTab");
    if (
      jobServerData.activeTab === activeTabClient &&
      jobServerData.activeTab === "applied"
    ) {
      setAppliedJobData((prevData: AppliedPageDataType) => {
        const applicationId = params.get("applicationId");
        const jobId = applicationId && params.get("jobId");
        const indexOfJob =
          (jobId &&
            jobServerData?.jobData?.data?.findIndex(
              (job: AppliedJobAPIKeysType) =>
                String(job?.jobDetails?.jobId) === String(jobId),
            )) ||
          -1;
        const appliedTempData = {
          ...prevData,
          jobDescIndex: !jobId
            ? { selectedIndex: isMobile ? undefined : 0 }
            : indexOfJob == -1
              ? { selectedIndex: null }
              : { selectedIndex: indexOfJob },
          showMobileModal: (jobId && isMobile) || false,
          activeTab: jobServerData.activeTab,
          urlQueries: {
            jobId: jobId || undefined,
            applicationId: applicationId || undefined,
          },
        };
        return appliedTempData;
      });
    }
  }, [jobServerData, searchParams]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const jobId = params.get("applicationId") && params.get("jobId");
    const indexOfJob =
      (jobId &&
        jobServerData?.jobData?.data?.findIndex(
          (job: AppliedJobAPIKeysType) =>
            String(job?.jobDetails?.jobId) === String(jobId),
        )) ||
      -1;
    setAppliedJobData((prevData: AppliedPageDataType) => ({
      ...prevData,
      jobDescIndex: !jobId
        ? { selectedIndex: isMobile ? undefined : 0 }
        : indexOfJob == -1
          ? { selectedIndex: null }
          : { selectedIndex: isMobile ? undefined : indexOfJob },
    }));
  }, [isMobile]);

  useEffect(() => {
    setTimeout(() => {
      setLoader(false);
    }, 0);
    handleEventTracking({
      eventName: "Pageviewed",
      eventType: "eventPush",
      eventProps: {
        page_name:
          cleverTapPropertyValues[
            appliedJobData.activeTab as keyof typeof cleverTapPropertyValues
          ],
      },
    });
  }, []);

  const RenderComponent = ({ name }: { name: DashboardSectionKey }) => {
    const Component = appliedPageMap[name];

    if (!Component) {
      return null;
    }

    return (
      <ErrorBoundary
        message={`Failed to load ${name.toLowerCase().replace(/_/g, " ")}`}
      >
        <Component />
      </ErrorBoundary>
    );
  };

  const renderSection = useCallback(
    (section: ColumnType[]) => {
      return section.map((row, rowIndex) => (
        <div key={rowIndex} className="flex w-full" id={row?.row[0]?.component}>
          {row.row.map((item, itemIndex) => (
            <div
              key={itemIndex}
              className="w-full"
              style={{
                ...item.styles,
                width: item.width,
                padding: item.padding,
              }}
            >
              <RenderComponent name={item.component} />
            </div>
          ))}
        </div>
      ));
    },
    [isMobile],
  );
  const renderRightcolumn = <div>{renderSection(layout.rightColumn)}</div>;
  return (
    <AppliedPageContext.Provider value={contextValue}>
      <div className={`${loader ? "hidden" : ""}`}>
        <div
          className="box-border flex h-full w-auto flex-col md:m-auto md:!flex-row md:items-start md:!gap-6"
          id="application-dashboard-content"
        >
          {layout.leftColumn && (
            <div
              className="sticky z-[1001] md:!z-20 md:flex md:!w-[288px] md:shrink-0 md:grow-0"
              id="applied-dashboard-filters"
            >
              {renderSection(layout.leftColumn)}
            </div>
          )}
          {layout.middleColumn && (
            <div className={isMobile ? "w-full px-4 pb-10" : "md:!w-[560px]"}>
              {renderSection(layout.middleColumn)}
            </div>
          )}
          {layout.rightColumn && (!isMobile ? renderRightcolumn : "")}
        </div>
      </div>
    </AppliedPageContext.Provider>
  );
};

export default React.memo(AppliedPage);
